import { MultiSelectComboboxField } from '@/components/form-components/MultiSelectCombobox'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks'
import { deriveRoleIdsFromRoleNumber, deriveRoleNumberFromRoleIds, roleCanBeAssigned, RolesArray } from '@/util/roles'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { useCurrentOrganisation } from '../useCurrentOrganisation'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'

// Form validation schema
const formSchema = z.object({
  email: z.string().email('Invalid email address').min(1, 'Email is required').max(100, 'Email too long'),
  givenName: z.string().min(1, 'Given name is required').max(100, 'Given name too long'),
  familyName: z.string().min(1, 'Family name is required').max(100, 'Family name too long'),
  roleIdsArray: z.array(z.number()).min(1, 'At least one role must be assigned'),
})

export type TOrgUserForm = z.infer<typeof formSchema>

type MessagesSubscriptionsOrganisationUser = RestSchemas['Messages.Subscriptions.OrganisationUser']
type Roles = RestSchemas['Common.Roles']

type OrgUserFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedUser?: MessagesSubscriptionsOrganisationUser
  organisationId?: number
  refetchOrganisationUsers?: () => void
}

export const OrgUserForm = (props: OrgUserFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)

  const { onSubmit: onSubmitProp, ...rest } = props
  const onSubmit = useCallback(() => {
    onSubmitProp?.()
    setSheetOpen(false)
  }, [onSubmitProp])

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...rest} onSubmit={onSubmit} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...rest} onSubmit={onSubmit} />
  )
}

const FormComponent = (props: OrgUserFormProps) => {
  const { id: organisationId } = useCurrentOrganisation(props.organisationId)
  const edit = !!props.selectedUser
  const { selectedUser, onSubmit, refetchOrganisationUsers } = props
  const queryClient = useQueryClient()

  const { putSubscriptionsApiAddUser, postSubscriptionsApiUpdateUser } = useRestClient()

  const { mutate: addUser } = putSubscriptionsApiAddUser.useMutation()
  const { mutate: updateUser } = postSubscriptionsApiUpdateUser.useMutation()

  const [userRole] = useSessionStorage('roles', 1)
  const disabledRoleIds = RolesArray.filter((x) => !roleCanBeAssigned(x.roleId, userRole)).map((x) => x.roleId)
  const canEditEmail = !edit // Can only set email on create

  // Create role options, filtering out disabled roles
  const roleOptions = RolesArray.filter((role) => !disabledRoleIds.includes(role.roleId)).map((role) => ({
    value: role.roleId,
    label: role.roleName,
    description: role.roleDescription,
  }))

  const defaultValues = useMemo(() => {
    if (edit && selectedUser) {
      return {
        email: selectedUser.email || '',
        givenName: selectedUser.givenName || '',
        familyName: selectedUser.familyName || '',
        roleIdsArray: selectedUser.roles ? deriveRoleIdsFromRoleNumber(selectedUser.roles) : [1],
      }
    }
    return {
      email: '',
      givenName: '',
      familyName: '',
      roleIdsArray: [1],
    }
  }, [edit, selectedUser])

  const form = useForm<TOrgUserForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const onMutationSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['getSubscriptionsApiListAllPlatformAdmins'] })

    if (refetchOrganisationUsers) {
      refetchOrganisationUsers()
    }

    toast.success(edit ? 'User successfully updated' : 'User successfully added')
    onSubmit?.()
    form.reset()
  }, [edit, onSubmit, queryClient, refetchOrganisationUsers, form])

  const handleSubmit = useCallback(
    async (data: TOrgUserForm) => {
      const payload = {
        id: 0, // Default ID for new user
        active: true,
        isFrozen: false,
        teamCount: 0,
        directLinkCount: 0,
        profilePicture: selectedUser?.profilePicture ?? '',
        organisationId,
        givenName: data.givenName,
        familyName: data.familyName,
        email: data.email,
        roles: deriveRoleNumberFromRoleIds(data.roleIdsArray) as Roles,
      }

      if (edit && selectedUser?.id) {
        updateUser(
          { body: { ...payload, id: selectedUser.id } },
          {
            onSuccess: onMutationSuccess,
            onError: (error) => {
              console.error(error)
              toast.error('Failed to update user')
            },
          }
        )
      } else {
        addUser(
          { body: payload },
          {
            onSuccess: onMutationSuccess,
            onError: (error) => {
              console.error(error)
              toast.error('Failed to add user')
            },
          }
        )
      }
    },
    [organisationId, selectedUser, edit, addUser, updateUser, onMutationSuccess]
  )

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter email address"
                        readOnly={!canEditEmail}
                        disabled={!canEditEmail}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="givenName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Given Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter given name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="familyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Family Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter family name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <MultiSelectComboboxField
                control={form.control}
                name="roleIdsArray"
                label="Roles"
                options={roleOptions}
                placeholder="Select roles..."
                searchPlaceholder="Search roles..."
                emptyMessage="No roles found."
              />
            </div>
            <div className="form-action-container">
              <Button type="submit">{edit ? 'Update User' : 'Add User'}</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
